// Single source of truth for whether the Agreements step can advance, and — when
// it cannot — what to tell the agent.
//
// WHY THIS IS ITS OWN MODULE: these were two separate hand-maintained expressions
// of the same rule, `canContinue` and the footer's `nextLabel` chain, and they
// drifted. canContinue was recruit-aware; the label chain tested !ltSigned and
// !w9Done unconditionally. So a recruit whose ICA was signed — their only
// document — got "Sign Laughton Team agreements to continue" for a section that
// is not rendered for them, while canContinue was TRUE and the button worked.
// A label that contradicts the button is worse than a wrong label: it reads as a
// blocker, and it sent people to the "Already signed — skip" checkbox instead,
// masking the real signature state.
//
// Returning { ok, label } from one function makes that class of drift impossible:
// there is no way to change one without the other.
//
// Plain .js with no JSX on purpose, so it can be imported by a test runner
// (see backend/test/agreements-gate.test.js — the repo's only test setup).

/**
 * @param {object} s
 * @param {boolean} s.recruit        intake-pipeline agent (signs only the ICA)
 * @param {boolean} s.alreadySigned  the manual "already signed — skip" escape
 * @param {boolean} s.epSigned       ICA (recruit) or EveryPlace package (transfer)
 * @param {boolean} s.ltSigned       Laughton Team amendments — transfer-day only
 * @param {boolean} s.w9Done         W-9 — transfer-day only
 * @returns {{ ok: boolean, label: string }}
 */
export function agreementsGate({ recruit, alreadySigned, epSigned, ltSigned, w9Done }) {
  // The escape hatch outranks everything: it exists precisely for agents who
  // signed outside the wizard and whose state we therefore cannot observe.
  if (alreadySigned) return { ok: true, label: "Continue" };

  // Recruits sign exactly one document. Laughton amendments and the W-9 are
  // transfer-day only and are not even rendered here, so they must not be able
  // to hold a recruit back.
  if (recruit) {
    return epSigned
      ? { ok: true, label: "Continue" }
      : { ok: false, label: "Sign your ICA to continue" };
  }

  if (!epSigned) return { ok: false, label: "Sign EveryPlace agreements to continue" };
  if (!ltSigned) return { ok: false, label: "Sign Laughton Team agreements to continue" };
  if (!w9Done)   return { ok: false, label: "Complete your W-9 to continue" };
  return { ok: true, label: "Continue" };
}
